import React from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {useDispatch} from 'react-redux';
import {IPaginatedResult} from '../../../incase-domain/search';
import {ConnectionRequestStatus, IConnection, IConnectionRequest} from '../../../incase-domain/connection';
import Button from '../../../incase-domain/components/button/Button';
import {updateConnectionRequest} from '../../../service/connection.service';
import {IUpdateError} from '../../../store/actions/types/error.types';
import {setConnectionIdAction} from '../../../store/actions/connection.actions';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import DataView, {ViewMode} from '../../components/common/data/DataView';
import {PaginationPosition} from '../../components/common/data/pagination/Paginator';
import {createDataViewListStateProps, ListParams, SetListParamsOptions} from '../../../hooks/listParams';

type ConnectionsTableMode = 'received' | 'sent';

interface ConnectionsTableProps {
    mode: ConnectionsTableMode;
    data: UseQueryResult<IPaginatedResult<IConnectionRequest>>;
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
}

function ConnectionsTable({mode, data, listParams, setListParams}: ConnectionsTableProps): JSX.Element {
    const dataViewListState = createDataViewListStateProps(listParams, setListParams);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const updateConnectionRequestMutation = useMutation<
        IConnection,
        IUpdateError,
        {requestStatus: ConnectionRequestStatus; connectionId: string}
    >(
        async ({requestStatus, connectionId}) => {
            return updateConnectionRequest(connectionId, requestStatus);
        },
        {
            onSuccess: (dataResult) => {
                dispatch(setConnectionIdAction(dataResult._id));
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.auth.all});
                data.refetch();
            },
        }
    );

    const handleUpdateConnectionRequest = (requestStatus: ConnectionRequestStatus, connectionId: string): void => {
        updateConnectionRequestMutation.mutate({requestStatus, connectionId});
    };

    const renderUserImage = (): JSX.Element => (
        <Button leading="solar:user-linear" variant="soft" size="sm" rounded square disablePointerEvents />
    );

    const renderUsernameEmail = (connectionRequest: IConnectionRequest): JSX.Element => {
        switch (mode) {
            case 'received':
                return (
                    <>
                        <span>
                            <strong>{connectionRequest.senderEmail}</strong>
                        </span>
                        <span>
                            <p>{connectionRequest.senderEmail}</p>
                        </span>
                    </>
                );

            case 'sent':
                return <p>{connectionRequest.receiverEmail}</p>;

            default:
                return <></>;
        }
    };

    const renderActions = (connectionRequest: IConnectionRequest): JSX.Element => {
        switch (mode) {
            case 'received':
                return (
                    <div className="u-flex u-gap-2">
                        <Button
                            data-cy="acceptRequest"
                            variant="solid"
                            color="secondary"
                            leading="ph:check-bold"
                            size="xs"
                            onClick={() => handleUpdateConnectionRequest(ConnectionRequestStatus.ACCEPTED, connectionRequest._id)}
                            disabled={data.isFetching}
                        >
                            {t('common.accept')}
                        </Button>
                        <Button
                            variant="soft"
                            leading="ph:x-bold"
                            size="sm"
                            onClick={() => handleUpdateConnectionRequest(ConnectionRequestStatus.CANCELED, connectionRequest._id)}
                            disabled={data.isFetching}
                        >
                            {t('common.decline')}
                        </Button>
                    </div>
                );

            case 'sent':
                return (
                    <Button
                        leading="solar:trash-bin-minimalistic-linear"
                        size="sm"
                        color="error"
                        variant="soft"
                        onClick={() => handleUpdateConnectionRequest(ConnectionRequestStatus.CANCELED, connectionRequest._id)}
                        disabled={data.isFetching}
                    >
                        {t('common.remove')}
                    </Button>
                );

            default:
                return <></>;
        }
    };

    return (
        <DataView
            views={[
                {
                    columns: [
                        {body: () => renderUserImage(), width: '2rem'},
                        {label: t('connections.user'), body: (row) => renderUsernameEmail(row), width: '100%'},
                        {body: (row) => renderActions(row), width: mode === 'received' ? '14rem' : undefined},
                    ],
                    view: ViewMode.TABLE,
                    hideHeader: true,
                },
            ]}
            rows={data?.data?.content || []}
            rowIdentifier={(row) => row._id}
            loading={data.isLoading || data.isFetching}
            paginationData={{
                pageSize: listParams.size,
                currentPage: listParams.page,
                itemsCount: data?.data?.numberOfElements || 0,
            }}
            {...dataViewListState}
            hideToolbar
            paginationPosition={PaginationPosition.BOTTOM}
        />
    );
}

export default ConnectionsTable;
